<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, provide } from 'vue';
import { useNow } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useEmitter } from 'dashboard/composables/emitter';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import KanbanColumn from './KanbanColumn.vue';
import { loadKanbanState, saveKanbanState } from './kanbanState';

const props = defineProps({
  filterPayload: { type: Array, default: null },
  context: { type: Object, default: () => ({}) },
  accountId: { type: [Number, String], default: null },
  assignedOnly: { type: Boolean, default: false },
});
const emit = defineEmits(['openConversation', 'counts']);
const store = useStore();
const { t } = useI18n();
provide('tmkKanbanNow', useNow({ interval: 60_000 }));
const counts = ref({
  label_counts: {},
  total: 0,
  unlabeled_count: 0,
  unattended_count: 0,
  unassigned_count: 0,
});
const countStatus = ref('loading');
const moveFailed = ref(false);
const moving = ref(false);
const revision = ref(0);
const showColumns = ref(false);
const currentUserId = computed(() => store.getters.getCurrentUser?.id);
const storeLabels = computed(
  () => store.getters['labels/getKanbanLabels'] || []
);
const boardState = ref(loadKanbanState(currentUserId.value, props.accountId));
const filters = computed(() => ({
  ...props.context,
  filterPayload: props.filterPayload || undefined,
}));
const scopeKey = computed(() =>
  JSON.stringify([filters.value, revision.value])
);
let generation = 0;
let controller;
let refreshTimer;

const fetchCounts = async () => {
  generation += 1;
  const currentGeneration = generation;
  controller?.abort();
  controller = new AbortController();
  countStatus.value = 'loading';
  try {
    const response = await ConversationAPI.getLabelCounts({
      ...filters.value,
      signal: controller.signal,
    });
    if (generation !== currentGeneration) return;
    counts.value = response.data;
    emit('counts', response.data.label_counts || {});
    countStatus.value = 'ready';
  } catch (error) {
    if (generation === currentGeneration) countStatus.value = 'error';
  }
};

const availableColumns = computed(() => {
  const names = new Set([
    ...(props.assignedOnly ? [] : storeLabels.value.map(label => label.title)),
    ...Object.keys(counts.value.label_counts || {}),
  ]);
  const order = boardState.value.order || [];
  const result = [...names].map(name => ({
    key: name,
    label: name,
    title: name,
    realCount: counts.value.label_counts?.[name] ?? 0,
  }));
  result.push({
    key: '',
    label: null,
    title: t('CHAT_LIST.TMK.UNLABELED'),
    realCount: counts.value.unlabeled_count ?? 0,
  });
  result.sort((a, b) => {
    const aIndex = order.indexOf(a.key);
    const bIndex = order.indexOf(b.key);
    if (aIndex !== -1 || bIndex !== -1)
      return (
        (aIndex === -1 ? Infinity : aIndex) -
        (bIndex === -1 ? Infinity : bIndex)
      );
    if (!a.key || !b.key) return !a.key ? 1 : -1;
    return a.title.localeCompare(b.title);
  });
  return result;
});
const columns = computed(() =>
  availableColumns.value
    .filter(column => !boardState.value.hidden.includes(column.key))
    .map(column => ({
      ...column,
      collapsed: boardState.value.collapsed.includes(column.key),
    }))
);
const persist = () =>
  saveKanbanState(currentUserId.value, boardState.value, props.accountId);
const toggleState = (field, label) => {
  const key = label ?? '';
  const values = new Set(boardState.value[field]);
  if (values.has(key)) values.delete(key);
  else values.add(key);
  boardState.value = { ...boardState.value, [field]: [...values] };
  persist();
};
const onMoveColumn = ({ label, dir }) => {
  const order = availableColumns.value.map(column => column.key);
  const index = order.indexOf(label ?? '');
  const next = index + (dir === 'left' ? -1 : 1);
  if (index < 0 || next < 0 || next >= order.length) return;
  [order[index], order[next]] = [order[next], order[index]];
  boardState.value = { ...boardState.value, order };
  persist();
};
const onDropCard = async ({ toLabel, fromLabel, conversationId }) => {
  if (moving.value || toLabel === fromLabel) return;
  moving.value = true;
  moveFailed.value = false;
  try {
    // The server changes only the source/target labels under a row lock.
    await ConversationAPI.moveKanbanCard({
      conversationId,
      fromLabel,
      toLabel,
    });
    revision.value += 1;
  } catch (error) {
    moveFailed.value = true;
  } finally {
    moving.value = false;
  }
};
const refresh = () => {
  if (refreshTimer) return;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    revision.value += 1;
  }, 1000);
};
useEmitter('fetch_conversation_stats', refresh);
useEmitter('WEBSOCKET_RECONNECT_COMPLETED', refresh);
watch(scopeKey, fetchCounts);
watch(
  () => props.accountId,
  () => {
    boardState.value = loadKanbanState(currentUserId.value, props.accountId);
  }
);
onMounted(fetchCounts);
onBeforeUnmount(() => {
  generation += 1;
  controller?.abort();
  clearTimeout(refreshTimer);
});
</script>

<template>
  <div
    class="flex flex-col h-full w-full min-w-0 overflow-hidden"
    :aria-busy="moving"
  >
    <div
      data-testid="kanban-filter-bar"
      class="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-n-weak"
    >
      <div
        data-testid="kanban-summary"
        class="flex flex-wrap items-center gap-4 text-xs text-n-slate-11 tabular-nums"
      >
        <span
          >{{ t('CHAT_LIST.TMK.TOTAL') }}:
          {{ countStatus === 'ready' ? counts.total : '…' }}</span
        >
        <span
          >{{ t('CHAT_LIST.TMK.UNATTENDED') }}:
          {{ countStatus === 'ready' ? counts.unattended_count : '…' }}</span
        >
        <span v-if="!assignedOnly"
          >{{ t('CHAT_LIST.TMK.UNASSIGNED') }}:
          {{ countStatus === 'ready' ? counts.unassigned_count : '…' }}</span
        >
      </div>
      <button
        class="flex items-center gap-1.5 text-xs text-n-slate-11 hover:text-n-slate-12"
        :aria-expanded="showColumns"
        @click="showColumns = !showColumns"
      >
        <span class="i-lucide-columns-3 size-4" />{{
          t('CHAT_LIST.TMK.COLUMNS')
        }}
      </button>
    </div>
    <div
      v-if="showColumns"
      class="flex flex-wrap gap-3 border-b border-n-weak p-3"
    >
      <label
        v-for="column in availableColumns"
        :key="column.key"
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
      >
        <input
          type="checkbox"
          :checked="!boardState.hidden.includes(column.key)"
          @change="toggleState('hidden', column.label)"
        />{{ column.title }}
      </label>
    </div>
    <div
      v-if="moveFailed || countStatus === 'error'"
      role="alert"
      class="flex items-center justify-between gap-2 p-3 text-sm text-n-ruby-11 bg-n-ruby-2"
    >
      {{
        moveFailed ? t('CHAT_LIST.TMK.MOVE_ERROR') : t('CHAT_LIST.FETCH_ERROR')
      }}
      <button
        v-if="countStatus === 'error'"
        class="underline"
        @click="fetchCounts"
      >
        {{ t('CHAT_LIST.TMK.RETRY') }}
      </button>
    </div>
    <div
      :key="JSON.stringify(filters)"
      class="flex gap-4 p-4 overflow-x-auto flex-1 min-h-0 min-w-0 bg-n-slate-1"
    >
      <KanbanColumn
        v-for="column in columns"
        :key="column.key"
        :label="column.label"
        :title="column.title"
        :real-count="column.realCount"
        :count-status="countStatus"
        :filters="filters"
        :refresh-token="revision"
        :collapsed="column.collapsed"
        @drop-card="onDropCard"
        @card-click="$emit('openConversation', $event)"
        @toggle-collapse="toggleState('collapsed', $event)"
        @move-column="onMoveColumn"
      />
    </div>
  </div>
</template>
